<script setup lang="ts">
import { ChevronLeft, ChevronRight, ExternalLink, FileQuestion, ImageIcon, RefreshCw } from "@lucide/vue";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import { isRunning, useJob } from "@/api/jobs";
import { enrichDocument, useDocument, useDocumentAssets } from "@/api/knowledge";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import MarkdownView from "@/components/common/MarkdownView.vue";
import PageHeader, { type Crumb } from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import TypeBadge from "@/components/knowledge/TypeBadge.vue";
import { Button } from "@/components/ui/button";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import { formatNumber } from "@/lib/format";
import { entityPath, typeInfo } from "@/lib/knowledge";

definePage({ meta: { title: "Document", requires: { capability: "knowledge.read" } } });

/** Rendering (markdown + sanitising) a multi-megabyte body at once freezes the tab: show the first part, the rest on request. */
const PREVIEW_CHARS = 150_000;

const route = useRoute("/k/doc/[id]");
const doc = useDocument(() => route.params.id);
const d = computed(() => doc.data.value?.document);
const mentions = computed(() => (doc.data.value?.mentions ?? []).filter((m): m is { type: string; slug: string; name: string; uri: string } => "slug" in m));

const showAll = ref(false);
watch(() => route.params.id, () => (showAll.value = false));
const body = computed(() => d.value?.bodyMd ?? "");
const cut = computed(() => !showAll.value && body.value.length > PREVIEW_CHARS);
/** Cut at a paragraph boundary near the limit, so the preview does not end mid-table or mid-code block. */
const shown = computed(() => {
  if (!cut.value) return body.value;
  const at = body.value.lastIndexOf("\n\n", PREVIEW_CHARS);
  return body.value.slice(0, at > PREVIEW_CHARS * 0.8 ? at : PREVIEW_CHARS);
});

/** Knowledge › source › the folders of the document in its source › the document (the current page). */
const crumbs = computed<Crumb[]>(() => [
  { label: t.nav.knowledge, to: "/knowledge" },
  ...(d.value ? [{ label: d.value.source.name }, ...d.value.breadcrumbs.map((label) => ({ label })), { label: d.value.title }] : []),
]);
const parts = computed(() => d.value?.parts ?? []);
const partIndex = computed(() => parts.value.findIndex((p) => p.id === d.value?.id));
const prevPart = computed(() => (partIndex.value > 0 ? parts.value[partIndex.value - 1] : null));
const nextPart = computed(() => (partIndex.value >= 0 && partIndex.value < parts.value.length - 1 ? parts.value[partIndex.value + 1] : null));

// Uploaded documents: images without a caption / scans not read yet (AI was unavailable, over budget, or failed).
const { can } = useSession();
const assets = useDocumentAssets(() => route.params.id);
const waiting = computed(() => (assets.data.value?.items ?? []).filter((a) => a.pending.length || a.failed.length).length);
const enrichJobId = ref<string | null>(null);
const enrichJob = useJob(enrichJobId);
const enriching = computed(() => Boolean(enrichJobId.value) && (!enrichJob.data.value || isRunning(enrichJob.data.value)));
async function runEnrich() {
  try {
    enrichJobId.value = (await enrichDocument(route.params.id)).jobId;
    toast.success(t.knowledgeUpload.queued);
  } catch (err) {
    toast.error(errorMessage(err));
  }
}
watch(enriching, (now, before) => {
  if (before && !now) {
    void doc.refetch();
    void assets.refetch();
  }
});
</script>

<template>
  <div>
    <ListSkeleton v-if="doc.isPending.value" :rows="6" />
    <ErrorState v-else-if="doc.error.value" :error="doc.error.value" what="Document" @retry="doc.refetch()" />
    <template v-else-if="d">
      <PageHeader :title="d.title" :crumbs="crumbs">
        <template #badges><TypeBadge document /></template>
        <template #meta>
          <p class="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <RefreshCw class="size-3" aria-hidden="true" /> Synced from {{ d.source.name }} ({{ d.source.type }}) <RelativeTime :value="d.syncedAt" />
            <template v-if="d.labels?.length">
              <span aria-hidden="true">·</span>
              <span v-for="l in d.labels" :key="l">#{{ l }}</span>
            </template>
          </p>
        </template>
        <template v-if="d.url" #actions>
          <Button as="a" :href="d.url" target="_blank" rel="noopener noreferrer" variant="outline"><ExternalLink /> {{ t.knowledge.openOriginal }}</Button>
        </template>
      </PageHeader>
      <nav v-if="parts.length > 1" aria-label="Parts" class="mb-6 flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-sm">
        <span class="text-muted-foreground">{{ t.knowledge.documentPart(partIndex + 1, parts.length) }}</span>
        <Button v-if="prevPart" as-child variant="ghost" size="sm" class="h-7">
          <RouterLink :to="`/k/doc/${encodeURIComponent(prevPart.id)}`"><ChevronLeft /> Part {{ prevPart.part }}</RouterLink>
        </Button>
        <Button v-if="nextPart" as-child variant="ghost" size="sm" class="h-7">
          <RouterLink :to="`/k/doc/${encodeURIComponent(nextPart.id)}`">Part {{ nextPart.part }} <ChevronRight /></RouterLink>
        </Button>
      </nav>
      <div v-if="waiting" class="mb-6 flex flex-col gap-2 rounded-md border bg-muted/40 p-3 text-sm sm:flex-row sm:items-center">
        <ImageIcon class="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <p class="flex-1">{{ t.knowledgeUpload.pendingBanner }} ({{ waiting }})</p>
        <Button v-if="can('knowledge.write')" size="sm" variant="outline" :disabled="enriching" @click="runEnrich">{{ t.knowledgeUpload.runNow }}</Button>
      </div>
      <!-- grid-cols-1 (a minmax(0,1fr) track): an implicit auto column would grow to the widest unbreakable line -->
      <div class="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_17rem]">
        <article class="min-w-0">
          <EmptyState v-if="!body.trim()" :icon="FileQuestion" :title="t.knowledge.documentNoText" :description="t.knowledge.documentNoTextHint">
            <Button v-if="d.url" as="a" :href="d.url" target="_blank" rel="noopener noreferrer" variant="outline"><ExternalLink /> {{ t.knowledge.openOriginal }}</Button>
          </EmptyState>
          <template v-else>
            <MarkdownView :source="shown" />
            <div v-if="cut" class="mt-6 flex flex-col items-start gap-2 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground" role="note">
              <span>Showing the first {{ formatNumber(shown.length) }} of {{ formatNumber(body.length) }} characters.</span>
              <Button size="sm" variant="outline" @click="showAll = true">Show the whole document</Button>
            </div>
          </template>
        </article>
        <aside v-if="mentions.length" class="min-w-0">
          <SectionCard title="Mentions">
            <ul class="flex flex-col gap-0.5">
              <li v-for="m in mentions" :key="m.uri">
                <RouterLink :to="entityPath(m.type, m.slug)" class="-mx-1.5 flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted">
                  <component :is="typeInfo(m.type).icon" class="size-3.5 shrink-0" :style="{ color: typeInfo(m.type).color }" aria-hidden="true" />
                  <span class="truncate">{{ m.name }}</span>
                </RouterLink>
              </li>
            </ul>
          </SectionCard>
        </aside>
      </div>
    </template>
  </div>
</template>
